import { useState } from "react";
import { asEntry, type Exercise } from "../core/exercises";
import { updateSession, type Session, type SessionError, type SessionExercise } from "../core/sessions";
import type { Backend } from "../core/storage";
import { newTemplateExercise, removeSet, SET_TYPES, setLabels, updateSet, type TemplateExercise } from "../core/templates";
import { formatNumber, parseReps } from "../core/units";
import { ActionSheet } from "./ActionSheet";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { ExercisePicker } from "./ExercisePicker";
import { NumField } from "./NumField";
import { ExerciseCard } from "./TemplateEditor";
import { t } from "./texts";

const MIN = 60_000;

/** "2026-10-10T18:30" in local time, for a datetime-local field. */
const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** A stored session's sets are all done; the cards edit them as template sets and they come back done. */
const asCard = (x: SessionExercise): TemplateExercise =>
  ({ exerciseId: x.exerciseId, ...(x.measure ? { measure: x.measure } : {}), restSec: x.restSec, sets: x.sets });
const fromCard = (te: TemplateExercise): SessionExercise => ({ ...te, sets: te.sets.map((s) => ({ ...s, done: true })) });

/**
 * Edit a finished session (Strong lets you fix a logged workout): name, start and duration, notes, and per exercise
 * the sets with their type and values; sets and exercises can be added and removed. Records and charts are derived
 * from the sessions, so they follow at once.
 */
export function SessionEditor(props: {
  db: Backend;
  session: Session;
  exercises: Exercise[];
  onCreatedExercise(e: Exercise): Promise<void>;
  onSaved(s: Session): void;
  onCancel(): void;
}) {
  const [draft, setDraft] = useState<Session>(props.session);
  const [errors, setErrors] = useState<SessionError[]>([]);
  const [missing, setMissing] = useState<[number, number][]>([]);
  const [picking, setPicking] = useState(false);
  const [exerciseMenu, setExerciseMenu] = useState<number>();
  const [setMenu, setSetMenu] = useState<{ ex: number; set: number }>();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [busy, setBusy] = useState(false);
  const byId = new Map(props.exercises.map((e) => [e.id, e]));

  const change = (fn: (s: Session) => Session) => { setDraft(fn); setErrors([]); setMissing([]); };
  const changeExercise = (i: number, fn: (te: TemplateExercise) => TemplateExercise) =>
    change((s) => ({ ...s, exercises: s.exercises.map((x, j) => (j === i ? fromCard(fn(asCard(x))) : x)) }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(props.session);
  const cancel = () => (dirty ? setConfirmDiscard(true) : props.onCancel());
  useBack(cancel);

  const save = async () => {
    setBusy(true);
    const r = await updateSession(props.db, draft, props.exercises);
    setBusy(false);
    if (r.ok) props.onSaved(r.session);
    else { setErrors(r.errors); setMissing(r.missing); }
  };

  if (picking) {
    return (
      <ExercisePicker db={props.db} exercises={props.exercises} onCreated={props.onCreatedExercise} onCancel={() => setPicking(false)}
        onAdd={(list) => {
          change((s) => ({ ...s, exercises: [...s.exercises, ...list.map((e) => fromCard(newTemplateExercise(e)))] }));
          setPicking(false);
        }} />
    );
  }

  const durationMs = draft.endedAt - draft.startedAt;
  const menuEx = exerciseMenu !== undefined ? draft.exercises[exerciseMenu] : undefined;
  const menuSets = setMenu && draft.exercises[setMenu.ex]?.sets;
  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={cancel}>{t.cancel}</button>
        <button className="primary" disabled={busy} onClick={save}>{t.save}</button>
      </header>
      <h1>{t.history.titleEdit}</h1>

      <label className="label">{t.template.name}
        <input className="field" value={draft.name} onChange={(e) => { const name = e.target.value; change((s) => ({ ...s, name })); }} />
      </label>
      <div className="row">
        <label className="label">{t.history.start}
          <input className={"field" + (errors.includes("timeInvalid") ? " invalid" : "")} type="datetime-local" value={toLocalInput(draft.startedAt)}
            onChange={(e) => {
              const start = new Date(e.target.value).getTime();
              change((s) => ({ ...s, startedAt: start, endedAt: start + (s.endedAt - s.startedAt) }));
            }} />
        </label>
        <label className="label duration-field">{t.history.durationMin}
          <NumField label={t.history.durationMin} value={Number.isFinite(durationMs) ? Math.round(durationMs / MIN) : undefined} mode="numeric"
            parse={parseReps} format={formatNumber} missing={errors.includes("timeInvalid")}
            onChange={(v) => change((s) => ({ ...s, endedAt: v === undefined || Number.isNaN(v) ? NaN : s.startedAt + v * MIN }))} />
        </label>
      </div>
      {errors.includes("timeInvalid") && <p className="error">{t.history.timeInvalid}</p>}
      <label className="label">{t.run.notes}
        <textarea className="field" rows={2} value={draft.notes} placeholder={t.run.notesPlaceholder}
          onChange={(e) => { const notes = e.target.value; change((s) => ({ ...s, notes })); }} />
      </label>

      {draft.exercises.map((x, i) => (
        <ExerciseCard key={i} session te={asCard(x)} exercise={asEntry(byId.get(x.exerciseId), x)}
          missing={missing.filter(([a]) => a === i).map(([, b]) => b)}
          onMenu={() => setExerciseMenu(i)}
          onChange={(fn) => changeExercise(i, fn)}
          onSetMenu={(set) => setSetMenu({ ex: i, set })} />
      ))}

      <button className="secondary wide" onClick={() => setPicking(true)}>{t.template.addExercise}</button>
      {errors.includes("valueMissing") && <p className="error">{t.history.valueMissing}</p>}
      {errors.includes("valueInvalid") && <p className="error">{t.history.valueInvalid}</p>}
      {errors.includes("empty") && <p className="error">{t.history.empty}</p>}
      <button className="primary wide" disabled={busy} onClick={save}>{t.save}</button>

      {menuEx && exerciseMenu !== undefined && (
        <ActionSheet title={byId.get(menuEx.exerciseId)?.name ?? t.template.unknownExercise} onClose={() => setExerciseMenu(undefined)}
          actions={[
            { label: t.template.removeExercise, danger: true,
              onClick: () => { change((s) => ({ ...s, exercises: s.exercises.filter((_, j) => j !== exerciseMenu) })); setExerciseMenu(undefined); } },
          ]} />
      )}
      {setMenu && menuSets && (
        <ActionSheet title={`${t.template.colSet} ${setLabels(menuSets)[setMenu.set]}`} onClose={() => setSetMenu(undefined)}
          actions={[
            ...SET_TYPES.map((type) => ({
              label: (menuSets[setMenu.set]?.type === type ? "✓ " : "") + t.setType[type],
              onClick: () => { changeExercise(setMenu.ex, (te) => updateSet(te, setMenu.set, { type })); setSetMenu(undefined); },
            })),
            { label: t.template.removeSet, danger: true,
              onClick: () => { changeExercise(setMenu.ex, (te) => removeSet(te, setMenu.set)); setSetMenu(undefined); } },
          ]} />
      )}
      {confirmDiscard && (
        <ConfirmDialog text={t.template.discard} confirm={t.template.discardYes} danger
          onCancel={() => setConfirmDiscard(false)} onConfirm={props.onCancel} />
      )}
    </>
  );
}
